import { bind, Component, createRef, h, signal, tag } from "omi";
import debounce from "lodash-es/debounce";
import isEmpty from "lodash-es/isEmpty";

import { NWButton } from "@/components/atoms/nw-button";
import { NWIcon } from "@/components/atoms/nw-icon";
import { NWTooltip } from "@/components/atoms/nw-tooltip";
import { NWAvatar } from "@/components/base/nw-avatar";
import { addToClipboard } from "@/utils/clipboard";
import { openLoginModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { PortfolioStore } from "../../store";
import { PortfolioHeaderSkeleton } from "./skeleton";

type Props = {
  showAnonState?: boolean;
  showComingSoonState?: boolean;
};

@tag("pages-portfolio-ui-header")
export class PortfolioHeader extends Component<Props> {
  static css = [tailwind, `:host { width: 100%; }`];

  private editingTitle = signal(false);
  private editingDescription = signal(false);

  private titleRef = createRef<HTMLTextAreaElement>();
  private descriptionRef = createRef<HTMLTextAreaElement>();
  private hasBlurred = signal(false);

  private debouncedUpdateDetails = debounce(async (details: { title?: string; description?: string }) => {
    PortfolioStore.setPortfolioDetails(details);
  }, 500);

  private adjustTextareaHeight(textarea: HTMLTextAreaElement | null) {
    if (!textarea) return;
    textarea.style.height = "0px";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  @bind
  private handleTitleInput(e: Event) {
    if (!PortfolioStore.isEditable()) return;

    e.preventDefault();
    const textarea = e.target as HTMLTextAreaElement;
    this.adjustTextareaHeight(textarea);
    this.debouncedUpdateDetails({ title: textarea.value });
  }

  @bind
  private handleDescriptionInput(e: Event) {
    if (!PortfolioStore.isEditable()) return;

    e.preventDefault();
    const textarea = e.target as HTMLTextAreaElement;
    this.adjustTextareaHeight(textarea);
    this.debouncedUpdateDetails({ description: textarea.value });
  }

  private handleShareClick() {
    const link = PortfolioStore.getShareLink();
    if (!link) return;

    addToClipboard(link);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackPortfolioLinkCopied();
    });
  }

  private handleBackToMyPortfolio() {
    PortfolioStore.stopViewingOtherPortfolio();
  }

  ready(): void {
    const isEditable = PortfolioStore.isEditable();

    if (isEditable) {
      if (this.titleRef.current) {
        this.adjustTextareaHeight(this.titleRef.current);
      }
      if (this.descriptionRef.current) {
        this.adjustTextareaHeight(this.descriptionRef.current);
      }
    }
  }

  renderAnonEmptyState() {
    const onFocusHandler = (e: FocusEvent) => {
      e.preventDefault();
      (e.target as HTMLTextAreaElement)?.blur();
      openLoginModal();
    };

    return (
      <div className="pb-10 animate-fadeInFast space-y-10">
        <div className="space-y-2">
          <div className="relative">
            <textarea
              onFocus={onFocusHandler}
              maxLength={60}
              draggable={false}
              rows={1}
              className="text-4xl font-bold w-full bg-transparent focus:outline-none focus:ring-2 focus:ring-brand-300
                        rounded px-1 peer placeholder-gray-600 min-h-[3rem] resize-none overflow-hidden"
              placeholder="My portfolio"
            />
          </div>

          <div className="relative">
            <textarea
              onFocus={onFocusHandler}
              rows={3}
              className="text-lg text-secondary w-full bg-transparent focus:outline-none focus:ring-2 focus:ring-brand-300
                        rounded px-1 resize-none peer placeholder-gray-400 min-h-[4.5rem] overflow-hidden"
              placeholder="Add your portfolio title and description here"
            />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <NWTooltip
            placement="top"
            content="Portfolio link copied 👌"
            trigger="click"
            className="w-full"
            showDuration={2000}
          >
            <NWButton type="secondary" size="full" className="w-full" onClick={openLoginModal}>
              <div className="flex items-center justify-center space-x-2">
                <NWIcon name="share" width={20} height={20} />
                <span className="w-full">Share portfolio</span>
              </div>
            </NWButton>
          </NWTooltip>
        </div>
      </div>
    );
  }

  renderComingSoonState() {
    const title = PortfolioStore.portfolioTitle.value || "My Portfolio";
    return (
      <div className="animate-fadeInFast space-y-10 flex flex-col items-center justify-center">
        <div className="space-y-4">
          <div className="relative">
            <h3 className="text-4xl font-semibold break-words w-full">{title}</h3>
          </div>
        </div>
      </div>
    );
  }

  render({ showAnonState = false, showComingSoonState = false }: Props) {
    const isLoading = PortfolioStore.portfolioSlice.portfolio.isLoading.value;
    const isEditable = PortfolioStore.isEditable();
    const isOwner = PortfolioStore.isOwner();
    const hasTitle = !isEmpty(PortfolioStore.portfolioTitle.value);
    const hasDescription = !isEmpty(PortfolioStore.portfolioDescription.value);
    const hasDetails = hasTitle && hasDescription;

    return (
      <div className="mx-auto flex-col items-center sm:items-start justify-center md:grid md:grid-cols-4">
        {Store.user.isLoggedIn.value && !isOwner ? (
          <div className="flex justify-start items-start gap-1 animate-in fade-in duration-300">
            <NWButton type="text" size="fit" icon="chevron-left" iconSize="md" onClick={this.handleBackToMyPortfolio}>
              <NWAvatar />
              <p class="font-semibold ml-2 md:hidden lg:block">Back to my portfolio</p>
            </NWButton>
          </div>
        ) : (
          <div></div>
        )}

        <div className="max-w-md mx-auto w-full my-2 lg:w-screen col-span-2">
          {isLoading ? (
            <PortfolioHeaderSkeleton />
          ) : showAnonState ? (
            this.renderAnonEmptyState()
          ) : showComingSoonState ? (
            this.renderComingSoonState()
          ) : (
            <div className="pb-10 animate-fadeInFast">
              <div className="space-y-2">
                <div className="relative">
                  {isEditable ? (
                    <textarea
                      ref={el => {
                        this.titleRef.current = el;
                        if (el && hasTitle && !this.hasBlurred.value) {
                          this.hasBlurred.value = true;
                          setTimeout(() => {
                            el.blur();
                          }, 0);
                        }

                        this.adjustTextareaHeight(el);
                      }}
                      autoFocus={true}
                      onFocus={() => (this.editingTitle.value = true)}
                      onBlur={() => (this.editingTitle.value = false)}
                      value={PortfolioStore.portfolioTitle.value}
                      onInput={this.handleTitleInput}
                      maxLength={60}
                      draggable={false}
                      rows={1}
                      className="text-4xl font-bold w-full bg-transparent focus:outline-none
                        rounded px-1 peer placeholder-gray-600 min-h-[3rem] resize-none overflow-hidden"
                      placeholder="..."
                    />
                  ) : (
                    <h3 className="text-4xl font-bold break-words w-full">
                      {PortfolioStore.portfolioTitle.value || "My portfolio"}
                    </h3>
                  )}
                </div>

                <div className="relative">
                  {isEditable ? (
                    <textarea
                      ref={el => {
                        this.descriptionRef.current = el;
                        this.adjustTextareaHeight(el);
                      }}
                      onFocus={() => (this.editingDescription.value = true)}
                      onBlur={() => (this.editingDescription.value = false)}
                      value={PortfolioStore.portfolioDescription.value}
                      onInput={this.handleDescriptionInput}
                      rows={3}
                      className="text-lg text-secondary w-full bg-transparent focus:outline-none
                        rounded px-1 resize-none peer placeholder-gray-400 min-h-[4.5rem] overflow-hidden"
                      placeholder="Add your portfolio title and description here"
                    />
                  ) : (
                    <p className="text-lg text-secondary break-words w-full">
                      {PortfolioStore.portfolioDescription.value}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-1 items-center justify-center">
                <NWTooltip
                  placement="top"
                  content="Portfolio link copied 👌"
                  trigger="click"
                  shouldShow={Store.user.isLoggedIn.value && hasDetails}
                  className="w-full"
                  showDuration={2000}
                >
                  <NWTooltip
                    placement="top"
                    content="Update your title and description first!"
                    shouldShow={!hasDetails && isOwner}
                  >
                    <NWButton
                      type="secondary"
                      size="full"
                      className="w-full"
                      disabled={!hasDetails && isOwner}
                      onClick={this.handleShareClick}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <NWIcon name="share" width={20} height={20} />
                        <span className="w-full">Share portfolio</span>
                      </div>
                    </NWButton>
                  </NWTooltip>
                </NWTooltip>
              </div>
            </div>
          )}
        </div>
        <div></div>
      </div>
    );
  }
}
